import cn from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRegister } from '@/features/auth';
import { Button } from '@/shared/ui/Button';
import CheckboxCommon from '@/shared/ui/Checkbox/CheckboxCommon';

import { AuthInput } from './ui/AuthInput';
import { AuthLayout } from './ui/AuthLayout';
import {
  authFieldFullClassName,
  authFieldsGridClassName,
  authFormClassName,
  authServerErrorClassName,
} from './ui/authForm';
import { RegisterSuccess } from './ui/RegisterSuccess';
import { RecaptchaField } from './RecaptchaField';
import { createRegisterSchema, type RegisterFormData } from './registerSchema';

// Register на новом стеке. Визуальный референс — mcms-front/src/pages/Auth/SignUp.tsx,
// но поля под наш API Serious (RegisterRequest из @/types/api): дополнительно
// trc20 для платёжных реквизитов и контакт в поле skype (имя поля — серверный
// контракт; по просьбе менеджеров 2026-06-11 спрашиваем только Telegram,
// WMZ-инпут выпилен). ref_id берётся из ?r= в URL (реферальная программа).
// Схема полей — registerSchema.ts.

export function Register() {
  const { t } = useTranslation(['auth', 'common']);
  const registerSchema = useMemo(() => createRegisterSchema(t), [t]);
  const [searchParams] = useSearchParams();
  const refId = searchParams.get('r');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const registerMutation = useRegister();
  // Email успешной регистрации для deep-link в бот: берём из zod-выхода
  // (lowercased+trimmed — то, что реально ушло на сервер), а не из getValues.
  const [registeredEmail, setRegisteredEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError: setFormError,
    clearErrors,
    watch,
    setValue,
    getValues,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirm_password: '',
      skype: '',
      trc20: '',
      agree: false,
    },
  });

  const agreeValue = watch('agree');
  const handleAgreeChange = (checked: boolean) => {
    setValue('agree', checked);
  };

  const handleInputChange = (field: keyof RegisterFormData) => {
    if (field !== 'agree' && errors.root?.server) {
      clearErrors('root.server');
    }
    if (errors[field]) {
      clearErrors(field);
    }
  };

  const getShowError = (field: keyof RegisterFormData) =>
    !!errors[field] || !!errors.root?.server;

  const onSubmit = async (data: RegisterFormData) => {
    clearErrors('root.server');
    if (!data.agree) {
      setFormError('root.server', {
        type: 'manual',
        message: t('validation.agreeRequired'),
      });
      return;
    }
    const reCaptcha = recaptchaRef.current?.getValue();
    if (!reCaptcha) {
      setFormError('root.server', {
        type: 'manual',
        message: t('validation.recaptcha'),
      });
      return;
    }
    try {
      const outcome = await registerMutation.mutateAsync({
        name: data.name,
        email: data.email,
        password: data.password,
        confirm_password: data.confirm_password,
        skype: data.skype,
        trc20: data.trc20 || undefined,
        ref_id: refId ? Number(refId) : undefined,
        reCaptcha,
      });
      switch (outcome.kind) {
        case 'ok':
          // Успех — экран рендерится через isSubmitSuccessful + outcome (см. ниже).
          // Сбрасывать reCAPTCHA не нужно: сама форма больше не показывается.
          setRegisteredEmail(data.email);
          return;
        case 'validation':
          recaptchaRef.current?.reset();
          Object.entries(outcome.errors).forEach(([field, msgs]) => {
            if (!msgs?.length) return;
            const fld = field as keyof RegisterFormData;
            if (fld in getValues()) {
              setFormError(fld, { type: 'server', message: msgs[0] });
            } else if (field === 'reCaptcha') {
              setFormError('root.server', {
                type: 'server',
                message: msgs[0],
              });
            }
          });
          if (Object.keys(outcome.errors).length === 0) {
            setFormError('root.server', {
              type: 'manual',
              message: t('register.dataError'),
            });
          }
          break;
        case 'error':
          recaptchaRef.current?.reset();
          setFormError('root.server', {
            type: 'manual',
            message: t('register.failed'),
          });
          break;
      }
    } catch {
      recaptchaRef.current?.reset();
      setFormError('root.server', {
        type: 'manual',
        message: t('common:networkError'),
      });
    }
  };

  // Экран успеха — после отправки формы без ошибок (outcome.kind === 'ok').
  // react-hook-form ставит isSubmitSuccessful только если сам submit прошёл без
  // throw и без manual errors после.
  const showSuccess = isSubmitSuccessful && !errors.root?.server;

  if (showSuccess) {
    return <RegisterSuccess email={registeredEmail} />;
  }

  return (
    <AuthLayout
      title={t('register.title')}
      questionText={t('haveAccount')}
      linkTo="/login"
      linkText={t('signIn')}
      wide
      columnClassName="mb-20"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={authFormClassName()}
      >
        {errors.root?.server && (
          <div className={authServerErrorClassName()}>
            {errors.root.server.message}
          </div>
        )}

        {/* Сетка полей: в 'card' — две колонки, в 'split' обёртки как будто нет
            (display: contents), поля остаются прямыми элементами формы. */}
        <div className={authFieldsGridClassName()}>
          <div className="relative w-full">
            <AuthInput
              type="text"
              label={t('fields.name')}
              example={t('examples.name')}
              autoComplete="name"
              error={errors.name?.message}
              showError={getShowError('name')}
              {...register('name', { onChange: () => handleInputChange('name') })}
            />
          </div>

          <div className="relative w-full">
            <AuthInput
              type="email"
              label={t('fields.email')}
              example={t('examples.email')}
              autoComplete="email"
              error={errors.email?.message}
              showError={getShowError('email')}
              {...register('email', { onChange: () => handleInputChange('email') })}
            />
          </div>

          <div className="relative w-full">
            <AuthInput
              type="password"
              label={t('fields.password')}
              example={t('examples.newPassword')}
              autoComplete="new-password"
              error={errors.password?.message}
              showError={getShowError('password')}
              {...register('password', { onChange: () => handleInputChange('password') })}
            />
          </div>

          <div className="relative w-full">
            <AuthInput
              type="password"
              label={t('fields.repeatPassword')}
              example={t('examples.repeatPassword')}
              autoComplete="new-password"
              error={errors.confirm_password?.message}
              showError={getShowError('confirm_password')}
              {...register('confirm_password', {
                onChange: () => handleInputChange('confirm_password'),
              })}
            />
          </div>

          <div className={cn('relative w-full', authFieldFullClassName())}>
            <AuthInput
              type="text"
              label={t('fields.trc20')}
              example={t('examples.trc20')}
              error={errors.trc20?.message}
              showError={getShowError('trc20')}
              {...register('trc20', { onChange: () => handleInputChange('trc20') })}
            />
          </div>

          <div className={cn('relative w-full', authFieldFullClassName())}>
            <AuthInput
              type="text"
              label={t('fields.telegram')}
              example={t('examples.telegram')}
              error={errors.skype?.message}
              showError={getShowError('skype')}
              {...register('skype', { onChange: () => handleInputChange('skype') })}
            />
          </div>
        </div>

        {/* Заглушки под Условия и Политику — 1:1 с mcms-front SignUp.tsx
            (целевые статичные страницы появятся позже; наш /rules под
            RequireAuth, гостю недоступен).

            Расхождение с фронтендером в layout: у нас flex+items-start+gap-2
            вместо их justify-between. Причина: текст со ссылками без label
            у CheckboxCommon оставляет иконку без gap'а (gap-1 в компоненте
            включается ТОЛЬКО когда передан label prop). justify-between же
            разносит к краям и span растягивается во всю ширину, из-за чего
            текст переносится на 2 строки в нашем layout. items-start +
            явный gap-2 даёт стандартный pattern «I agree to terms» с
            чекбоксом на высоте первой строки. */}
        <div className="flex items-start gap-2">
          <CheckboxCommon
            color="primary"
            onChange={handleAgreeChange}
            checked={agreeValue}
          />
          <span className="text-content-auth-checkbox">
            {t('register.agreePrefix')}
            <span className="cursor-pointer text-content-auth-link hover:text-content-auth-link-hover">
              {t('register.terms')}
            </span>
            {t('register.agreeAnd')}
            <span className="cursor-pointer text-content-auth-link hover:text-content-auth-link-hover">
              {t('register.privacy')}
            </span>
          </span>
        </div>

        <div>
          <RecaptchaField ref={recaptchaRef} />
        </div>

        <div className="mt-[6px]">
          <Button
            type="submit"
            disabled={!agreeValue || isSubmitting}
            isLoading={isSubmitting}
            className="min-h-[48px] w-full !px-0"
          >
            {t('signUp')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
